import { INVENTORY } from "./constants";
import { BLOCKS, BLOCK_KEYS, FAMILIES, type Family } from "./blocks";
import { SHAPE_ORDER, shapeName } from "./shapes";
import { TIERS, TILES, isOpenState, type HeldTool, type TileId, type ToolKind, type ToolTier } from "./tiles";

/** A tool item: its tier then its kind, e.g. "stonePickaxe". */
export type ToolItemId = `${ToolTier}${"Pickaxe" | "Axe" | "Shovel" | "Sword"}`;

/** Ship machinery, by what it is: each is made in every family (see Family), a different item for each. */
export type Machine = "Engine" | "MediumEngine" | "LargeEngine" | "Furnace" | "Helm" | "Turret" | "Harpoon" | "Winch" | "Pump" | "BowGun";
/** A machine item: its family then the machine, e.g. "ironcladFurnace". */
export type MachineItemId = `${Family}${Machine}`;

/** Building blocks, by what they are: each is made in every family (see Family), a different item for each. */
export type Build = "Plank" | "Iron" | "Glass" | "Brick" | "Stone" | "Grating" | "Paving" | "Decking" | "DeckPlate" | "Wall" | "Ladder" | "Lattice" | "Brace" | "Railing";
/** A building block item: its family then the block, e.g. "ironcladPlank". */
export type BuildItemId = `${Family}${Build}`;

/**
 * Everything a player can carry. Block items place as a tile; materials
 * come from digging and go into crafting (and furnaces); tools dig.
 */
export type ItemId =
  | "rock"
  | "plank"
  | "iron"
  | "glass"
  | "hatch"
  | "workbench"
  | "chest"
  | "ironOre"
  | "coal"
  | "starbark"
  | "wood"
  | "fiber"
  | "crystal"
  | "water"
  | "lantern"
  | "sign"
  | "bed"
  | "door"
  | "crate"
  | "planter"
  | "table"
  | "stool"
  | "flag"
  | "bulb"
  | "spores"
  | "skyfish"
  | "rope"
  | "grapple"
  | "patchKit"
  | "whaleOil"
  | "ice"
  | "bark"
  | "heartwood"
  | "sap"
  | "livingHeart"
  | "sunGlass"
  | "emberOre"
  | "moss"
  | "lichen"
  | "glowcap"
  | "frostFlower"
  | "sapling"
  | "clay"
  | "sand"
  | "obsidian"
  | "salt"
  | "saltFish"
  | "scrap"
  | "wire"
  | "lens"
  | "coldCell"
  | "core"
  | "lensLamp"
  | "steampack"
  | ToolItemId
  | MachineItemId
  | BuildItemId;

/**
 * An item. Which blocks it places (and is dug back out of), and what natural
 * blocks give it, are on the blocks themselves (see blocks.ts, placingWays).
 */
export interface ItemDef {
  name: string;
  /** A tool: held in the selected quick-access slot, it digs some blocks at full speed (see TileDef.tool). */
  tool?: ToolKind;
  /** What a tool is made of: how fast it digs, how long it lasts (TIERS). */
  tier?: ToolTier;
  /** Engine-seconds of burn when stoked into a furnace (see FURNACE). */
  fuel?: number;
  /** Engine-seconds of water it adds to a furnace. */
  water?: number;
  /** What it's for, for the recipe book. */
  desc?: string;
  /** Health eating one gives back. */
  heals?: number;
  /** Something worn: which slot it goes in (see GearSlot). */
  slot?: GearSlot;
}

/**
 * Where things are worn: a hat or helmet, a coat or armour, trousers,
 * gloves, boots, and the back, where the steampack goes. One item in each.
 */
export type GearSlot = "head" | "body" | "legs" | "hands" | "feet" | "back";
export const GEAR_SLOTS: readonly GearSlot[] = ["head", "body", "legs", "hands", "feet", "back"];
export const GEAR_SLOT_NAME: Record<GearSlot, string> = { head: "Head", body: "Body", legs: "Legs", hands: "Hands", feet: "Feet", back: "Back" };

/** What someone is wearing: the item in each slot, if any. */
export type Gear = Partial<Record<GearSlot, ItemId>>;

const TOOL_KINDS: readonly ToolKind[] = ["pickaxe", "axe", "shovel", "sword"];
const TOOL_TIERS: readonly ToolTier[] = ["wood", "stone", "iron"];
const TIER_NAME: Record<ToolTier, string> = { wood: "Wooden", stone: "Stone", iron: "Iron" };
/** What each tier's heads are made of, and how much of it each kind takes (the handle is always 2 wood). */
const TIER_MATERIAL: Record<ToolTier, ItemId> = { wood: "wood", stone: "rock", iron: "iron" };
const HEAD_SIZE: Record<ToolKind, number> = { pickaxe: 3, axe: 3, shovel: 1, sword: 2 };

/** The item id of a tool of `kind` made of `tier`. */
export const toolItem = (tier: ToolTier, kind: ToolKind) => `${tier}${kind[0]!.toUpperCase()}${kind.slice(1)}` as ToolItemId;

const TOOLS = Object.fromEntries(
  TOOL_TIERS.flatMap((tier) => TOOL_KINDS.map((kind) => [toolItem(tier, kind), { name: `${TIER_NAME[tier]} ${kind}`, tool: kind, tier }])),
) as Record<ToolItemId, ItemDef>;

/** The item id of `machine` made in `family`. */
export const machineItem = (family: Family, machine: Machine) => `${family}${machine}` as MachineItemId;

/** Each machine: what it's called and for, and what it takes before its family changes that (see FAMILY_LOOK). */
const MACHINES: Record<Machine, { name: string; desc: string; needs: Inventory }> = {
  Engine: { name: "steam engine", desc: "Pushes the ship away from its exhaust. Faces away from the ship's middle unless you rotate it (T).", needs: { iron: 2, wood: 4 } },
  MediumEngine: { name: "medium steam engine", desc: "A 2x2 engine with two nozzles: the push of two small ones, for a little less. Faces away from the ship's middle unless you rotate it (T).", needs: { iron: 3, wood: 7 } },
  LargeEngine: { name: "large steam engine", desc: "A 3x3 engine with three nozzles: the push of three small ones, for less again. Faces away from the ship's middle unless you rotate it (T).", needs: { iron: 4, wood: 9 } },
  Furnace: { name: "furnace", desc: "Holds fuel and water for the steam engines.", needs: { iron: 4, rock: 2 } },
  Helm: { name: "helm", desc: "Fly the ship from here. A ship with a helm holds station when nobody flies it.", needs: { wood: 6, iron: 2 } },
  Turret: { name: "turret", desc: "A gun a crewmate mans (right click in its seat). Goes on ships and asteroids.", needs: { iron: 6, wood: 2 } },
  Harpoon: { name: "harpoon", desc: "Goes on a ship; man it like a turret (right click in its seat) and aim with the mouse. Click fires a line along the barrel: it anchors in rock (the ship stays anchored with nobody aboard), sticks in a whale or gas bag, or hooks another ship to tow. Hold to reel in; G lets go.", needs: { iron: 4, rope: 4, wood: 2 } },
  Winch: { name: "winch", desc: "Reels in loose blocks drifting near the ship and tows them. Right click to switch it on or off.", needs: { iron: 3, rope: 2, wood: 2 } },
  Pump: { name: "pump", desc: "Fills the furnaces with water while the ship is in water.", needs: { iron: 2, glass: 1 } },
  BowGun: { name: "bow gun", desc: "A fixed gun the pilot fires (click at the helm). Points along the ship; rotate (T) to point it back.", needs: { iron: 4, wood: 1 } },
};
const MACHINE_KINDS = Object.keys(MACHINES) as Machine[];

/**
 * What a family does to a machine: what it says about it, and what it takes.
 * Ironclad is armoured (half as much iron again, a hit tougher); brassworks
 * has its dials and sight glasses (a glass more); shipwright is timber round
 * its iron (half the iron, made up in wood and a rope, and it burns).
 */
const FAMILY_LOOK: Record<Family, { name: string; desc: string; needs: (n: Inventory) => Inventory }> = {
  ironclad: { name: "Ironclad", desc: "Riveted armour plate: a hit tougher than the others.", needs: (n) => ({ ...n, iron: (n.iron ?? 0) + Math.ceil((n.iron ?? 0) / 2) }) },
  brassworks: { name: "Brassworks", desc: "Polished brass and copper, dials and sight glasses.", needs: (n) => ({ ...n, glass: (n.glass ?? 0) + 1 }) },
  shipwright: {
    name: "Shipwright",
    desc: "Iron in a timber cradle: light on iron, but it burns.",
    needs: (n) => {
      const saved = Math.floor((n.iron ?? 0) / 2);
      return { ...n, iron: (n.iron ?? 0) - saved, wood: (n.wood ?? 0) + saved * 2, rope: (n.rope ?? 0) + 1 };
    },
  },
};

/** The item id of building block `build` made in `family`. */
export const buildItem = (family: Family, build: Build) => `${family}${build}` as BuildItemId;

/** Each building block: what it's called and for, and what makes `count` of it (in any family; a family changes only its look). */
const BUILDS: Record<Build, { name: string; desc: string; needs: Inventory; count: number; workbench?: true }> = {
  Plank: { name: "timber hull", desc: "The basic hull block. Light, but it burns. T turns it into a wedge or a slope, for pointed bows and roofs.", needs: { plank: 1 }, count: 1 },
  Iron: { name: "iron plate", desc: "Armoured hull block (takes 3 hits). T turns it into a wedge or a slope.", needs: { iron: 1 }, count: 1 },
  Glass: { name: "glass", desc: "A window. T turns it into a wedge or a slope.", needs: { glass: 1 }, count: 1 },
  Brick: { name: "brick", desc: "Fired clay: a hull block that doesn't burn and takes 2 hits. T turns it into a wedge or a slope.", needs: { clay: 2, coal: 1 }, count: 4, workbench: true },
  Stone: { name: "stone block", desc: "Rock cut square: a hull block that takes 2 hits, cheap where rock is. T turns it into a wedge or a slope.", needs: { rock: 4 }, count: 4, workbench: true },
  Grating: { name: "grating", desc: "A solid block you can see through: drains, deck gratings, hatch covers.", needs: { iron: 1 }, count: 2, workbench: true },
  Paving: { name: "paving", desc: "Fitted stones that follow the ground like grass does: paths and terraces on a rock.", needs: { rock: 2 }, count: 2 },
  Decking: { name: "decking", desc: "Timber boards laid along the ground, following its slope.", needs: { plank: 1 }, count: 1 },
  DeckPlate: { name: "deck plate", desc: "Riveted iron laid along the ground, following its slope. Takes 3 hits.", needs: { iron: 1 }, count: 1, workbench: true },
  Wall: { name: "back wall", desc: "The inside of a hull: you walk in front of it. Seals a ship's interior.", needs: { wood: 1 }, count: 2 },
  Ladder: { name: "ladder", desc: "Climb between decks.", needs: { wood: 2, fiber: 1 }, count: 2 },
  Lattice: { name: "lattice", desc: "See-through, in the background like a back wall, but it doesn't seal a ship: cages, catwalk sides, trellis.", needs: { wood: 1 }, count: 2 },
  Brace: { name: "brace", desc: "A timber frame. Living bark won't heal over within a few blocks of one: keeps your tunnels open.", needs: { wood: 2, bark: 1 }, count: 2 },
  Railing: { name: "railing", desc: "Along a deck's edge, in the background: shows where the deck ends.", needs: { wood: 1 }, count: 2 },
};
const BUILD_KINDS = Object.keys(BUILDS) as Build[];

const BUILD_ITEMS = Object.fromEntries(
  FAMILIES.flatMap((family) => BUILD_KINDS.map((b) => [buildItem(family, b), { name: `${FAMILY_LOOK[family].name} ${BUILDS[b].name}`, desc: BUILDS[b].desc }])),
) as Record<BuildItemId, ItemDef>;

const MACHINE_ITEMS = Object.fromEntries(
  FAMILIES.flatMap((family) => MACHINE_KINDS.map((m) => [machineItem(family, m), { name: `${FAMILY_LOOK[family].name} ${MACHINES[m].name}`, desc: `${MACHINES[m].desc} ${FAMILY_LOOK[family].desc}` }])),
) as Record<MachineItemId, ItemDef>;

export const ITEMS: Record<ItemId, ItemDef> = {
  rock: { name: "Rock", desc: "Building block for asteroid homes; heads stone tools; goes into furnaces." },
  plank: { name: "Plank", desc: "Sawn timber: made into timber hull (in any family) and decking." },
  iron: { name: "Iron plate", desc: "Smelted from ore and coal. Goes into most machinery, and is made into iron plate hull (in any family) and deck plate." },
  glass: { name: "Glass", desc: "Made from void crystal, sun-glass, sand or obsidian. Goes into lanterns and pumps, and is made into windows (in any family)." },
  hatch: { name: "Hatch", desc: "A 2x4 door in the hull; rotate (T) to lay it flat (4x2) for floors." },
  workbench: { name: "Workbench", desc: "Stand by one to craft the harder recipes." },
  chest: { name: "Chest", desc: "Holds items. Dig it to take everything in it." },
  ironOre: { name: "Iron ore", desc: "Smelt into iron plate at a workbench (with coal). Needs a stone pickaxe to dig. Richest in the hot inner rings, near the suns, and further out from the start; now and then a small rock is almost all ore." },
  coal: { name: "Coal", fuel: 90, desc: "The best common fuel. Smelts iron. Most of it in the middle rings, where the trees grow; now and then a small rock is almost all coal." },
  starbark: { name: "Starbark", fuel: 30, desc: "Lantern-tree trunk. Cut into wood by hand; burns too." },
  wood: { name: "Wood", fuel: 8, desc: "Planks, walls, ladders, handles, furniture." },
  fiber: { name: "Frond fibre", desc: "Ladders, rope, beds, flags." },
  crystal: { name: "Void crystal", desc: "Makes glass. Needs an iron pickaxe. Lines the hollow hearts (geodes) of rocks, most often in the cold outer rings and round the blue suns." },
  water: { name: "Water", water: 60, desc: "Feed it to a furnace for steam, or throw it on a fire." },
  lantern: { name: "Lantern", desc: "Lights up the dark around it: ship interiors, caves." },
  sign: { name: "Sign", desc: "Leave a note. Hover over a sign to read it." },
  bed: { name: "Bed", desc: "Sleep in it (right click) to wake up there when you're knocked out." },
  door: { name: "Door", desc: "A cheap 1-wide door; put two side by side for a doorway. They open together." },
  crate: { name: "Cargo crate", desc: "A 1x1 chest for one kind of thing, up to 300 of it." },
  planter: { name: "Planter", desc: "Decoration: a little garden." },
  table: { name: "Table", desc: "Decoration." },
  stool: { name: "Stool", desc: "Decoration." },
  flag: { name: "Flag", desc: "Decoration: fly your colours." },
  bulb: { name: "Glow bulb", desc: "Lantern- or dusk-tree fruit. Makes lanterns." },
  spores: { name: "Spores", heals: 25, desc: "Food: eat one (H) to heal." },
  skyfish: { name: "Skyfish", heals: 35, desc: "Food: eat one (H) to heal. Caught from the schools circling the green rocks; they scatter if you rush them." },
  rope: { name: "Rope", desc: "Twisted frond fibre. Grapples and winches." },
  grapple: { name: "Grapple", desc: "Hold it and right click to throw a line at rock or a ship (about 50 feet); hold W to reel yourself in. Right click again or jump to let go. Out in open space, it's how you get anywhere." },
  patchKit: { name: "Patch kit", desc: "Right click a cracked block on a ship to mend it." },
  whaleOil: { name: "Whale oil", fuel: 400, desc: "From space whales. Burns four times as long as coal." },
  ice: { name: "Ice", water: 150, desc: "Feeds a furnace like two and a half waters. Mined from ice rocks, common in the cold outer rings." },
  bark: { name: "Living bark", fuel: 20, desc: "From living rocks near the suns. Makes braces and rope." },
  heartwood: { name: "Heartwood", fuel: 60, desc: "The hard core of a big living rock. Cut into lots of wood." },
  sap: { name: "Sap", desc: "Tapped from living rocks. Sticky: mends hulls (patch kits)." },
  livingHeart: { name: "Living heart", fuel: 2000, desc: "The heart of an old living rock. Taking it killed the rock. Burns for an age." },
  sunGlass: { name: "Sun-glass", desc: "Rock melted glassy by the suns' flares. Makes a lot of glass." },
  emberOre: { name: "Ember ore", fuel: 200, desc: "Hot ore from rock scorched by the suns. Burns hotter than coal. Needs an iron pickaxe." },
  moss: { name: "Moss", heals: 10, desc: "From rocks in dim light. Medicine: eat one (H) to heal a little." },
  lichen: { name: "Lichen", desc: "Crusts from rocks in poor light. A dye: colours flags." },
  glowcap: { name: "Glowcap", desc: "Glow fungus from dark caves. Makes a lantern without a glow bulb." },
  frostFlower: { name: "Frost flower", heals: 60, desc: "Rare and fragile, from frozen rocks. Heals a lot." },
  clay: { name: "Clay", desc: "Dug from pockets under the wet rocks. Fired with coal at a workbench into brick." },
  sand: { name: "Sand", desc: "From the dry side of meadows. Melts into glass at a workbench, with coal." },
  obsidian: { name: "Obsidian", desc: "Black volcanic glass from the cores of scorched rocks. Needs an iron pickaxe. Cuts into a lot of glass." },
  salt: { name: "Salt", desc: "Crust from the barren rocks. Salts a skyfish so it does you more good." },
  saltFish: { name: "Salt fish", heals: 60, desc: "A salted skyfish. Food: eat one (H) to heal a lot." },
  sapling: { name: "Sapling", desc: "Plant it on grass or moss (right click) and a lantern tree grows there." },
  // From the night: what the things that come in when a sun goes out leave behind. Dead in the sun's light.
  scrap: { name: "Scrap", desc: "Plate and rivets off the things that come in when a sun goes out. Smelts into iron at a workbench, with coal." },
  wire: { name: "Wire", desc: "Cold drawn wire off an eel or a spider. Twists into rope that doesn't burn." },
  lens: { name: "Lens", desc: "Glass ground by no hand in here, off a carrier or a warden. Put one in front of a lantern and it throws its light twice as far." },
  coldCell: { name: "Cold cell", desc: "A sealed cell from outside the sphere, dead under the suns' light. Worth something to someone, one day." },
  core: { name: "Warden core", desc: "A warden's heart. Put it down and its cold glow never goes out." },
  steampack: { name: "Steampack", slot: "back", desc: "Wear it (put it on in your inventory) and hold Shift to blast steam: it lifts you against gravity for a few seconds a tank, refilling on solid ground, and in open space it flies you about for free. Without one, out there, you drift." },
  lensLamp: { name: "Lens lamp", desc: "A lantern behind a lens: lights twice as far. The best thing to hold the night off with." },
  ...TOOLS,
  ...MACHINE_ITEMS,
  ...BUILD_ITEMS,
};

export const ITEM_IDS = Object.keys(ITEMS) as ItemId[];

export function isItemId(x: unknown): x is ItemId {
  return typeof x === "string" && Object.prototype.hasOwnProperty.call(ITEMS, x);
}

/** One way an item can be put down: the tile it places, its shape (see shapes.ts; 0 or missing for whole), and what that way is called (if it has more than one). */
export interface PlacingWay {
  tile: TileId;
  shape?: number;
  way?: string;
}

/**
 * The ways an item places, in the order T turns through them, the first
 * being its default: its blocks (not open states: an open hatch is dug back
 * into a hatch, but a hatch is put down shut), and for a block that takes
 * shapes, each shape in turn (see SHAPE_ORDER).
 */
const WAYS: Partial<Record<ItemId, PlacingWay[]>> = {};
for (const key of BLOCK_KEYS) {
  const b = BLOCKS[key];
  if (!b.item || isOpenState(key)) continue;
  const ways = (WAYS[b.item] ??= []);
  if (b.shapes) for (const shape of SHAPE_ORDER) ways.push(shape ? { tile: b.id, shape, way: shapeName(shape) } : { tile: b.id, way: shapeName(shape) });
  else ways.push({ tile: b.id, way: b.way });
}

export const placingWays = (item: ItemId): readonly PlacingWay[] => WAYS[item] ?? [];

/** The tile an item places by default, if it places one. */
export const placesAs = (item: ItemId): TileId | undefined => WAYS[item]?.[0]?.tile;

/** Every item that places as a block. */
export const HOTBAR: readonly ItemId[] = ITEM_IDS.filter((id) => placesAs(id) !== undefined);

/** The item a tile is placed by, and dug back out as (any tile of a piece is that piece's item). */
export const itemForTile = (tile: TileId): ItemId | undefined => TILES[tile]?.item;

/** What digging out a tile gives you: a natural block's drop, or a built block itself. */
export const dropFor = (tile: TileId): ItemId | undefined => TILES[tile]?.drop;

/** The tile whose art stands for an item: the block it places, or else the natural block it's dug out of (none for tools and the like). */
export function iconTile(item: ItemId): TileId | undefined {
  const placed = placesAs(item);
  if (placed !== undefined) return placed;
  const natural = TILES.findIndex((def, t) => t > 0 && !def.buildable && def.drop === item);
  return natural > 0 ? natural : undefined;
}

/** Item counts. Missing means none. */
export type Inventory = Partial<Record<ItemId, number>>;

export const count = (inv: Inventory, id: ItemId): number => inv[id] ?? 0;

export function hasAll(inv: Inventory, needs: Inventory): boolean {
  return (Object.keys(needs) as ItemId[]).every((id) => count(inv, id) >= (needs[id] ?? 0));
}

export function addItems(inv: Inventory, items: Inventory, sign = 1): void {
  for (const id of Object.keys(items) as ItemId[]) {
    const n = count(inv, id) + sign * (items[id] ?? 0);
    if (n > 0) inv[id] = n;
    else delete inv[id];
  }
}

/** Stacks an inventory takes up: each kind of thing needs one per INVENTORY.stack of it. */
export function slotsUsed(inv: Inventory): number {
  let n = 0;
  for (const id of Object.keys(inv) as ItemId[]) n += Math.ceil(count(inv, id) / INVENTORY.stack);
  return n;
}

/** Would `items` fit in `inv` (after taking `less` out)? See INVENTORY. */
export function roomFor(inv: Inventory, items: Inventory, less: Inventory = {}): boolean {
  const after = { ...inv };
  addItems(after, less, -1);
  addItems(after, items);
  return slotsUsed(after) <= INVENTORY.slots;
}

/** How many of `item` would still fit in `inv`. */
export function roomForCount(inv: Inventory, item: ItemId): number {
  const free = INVENTORY.slots - slotsUsed(inv);
  const topUp = (INVENTORY.stack - (count(inv, item) % INVENTORY.stack)) % INVENTORY.stack;
  return Math.max(0, free) * INVENTORY.stack + topUp;
}

/** The slot `item` is worn in, if it's worn at all. */
export const slotOf = (item: ItemId | null | undefined): GearSlot | undefined => (item ? ITEMS[item].slot : undefined);

/**
 * Put on one `item` out of `inv`, into its slot; whatever was worn there
 * goes back in the inventory. Returns why it can't, or null when it's done.
 */
export function equip(inv: Inventory, gear: Gear, item: ItemId): string | null {
  const slot = slotOf(item);
  if (!slot) return "can't be worn";
  if (count(inv, item) < 1) return "you don't have one";
  const was = gear[slot];
  if (was === item) return "already wearing one";
  if (was && !roomFor(inv, { [was]: 1 }, { [item]: 1 })) return "no room to carry what you're wearing";
  addItems(inv, { [item]: 1 }, -1);
  if (was) addItems(inv, { [was]: 1 });
  gear[slot] = item;
  return null;
}

/** Take off what's worn in `slot`, back into `inv`. Returns why it can't, or null when it's done. */
export function unequip(inv: Inventory, gear: Gear, slot: GearSlot): string | null {
  const was = gear[slot];
  if (!was) return "nothing there";
  if (!roomFor(inv, { [was]: 1 })) return "no room to carry it";
  addItems(inv, { [was]: 1 });
  delete gear[slot];
  return null;
}

/** Do they have a steampack on their back (see STEAMPACK)? */
export const wearsSteampack = (gear: Gear | undefined): boolean => gear?.back === "steampack";


/** What a new player starts with: a grapple, on a rock in the start system. Everything else is dug, cut and made. */
export const STARTER_KIT: Inventory = { grapple: 1 };

export interface Recipe {
  id: string;
  makes: ItemId;
  count: number;
  needs: Inventory;
  /** Only within reach of a workbench. */
  workbench?: boolean;
}

/**
 * Starbark is cut into wood by hand, and wood makes the wooden things. Simple
 * things are made by hand; the rest need a workbench within reach.
 */
export const RECIPES: readonly Recipe[] = [
  { id: "wood", makes: "wood", count: 4, needs: { starbark: 1 } },
  { id: "plank", makes: "plank", count: 2, needs: { wood: 2 } },
  { id: "workbench", makes: "workbench", count: 1, needs: { wood: 6 } },
  { id: "chest", makes: "chest", count: 1, needs: { wood: 8 } },
  { id: "iron", makes: "iron", count: 2, needs: { ironOre: 2, coal: 1 }, workbench: true },
  { id: "glass", makes: "glass", count: 3, needs: { crystal: 1 }, workbench: true },
  { id: "hatch", makes: "hatch", count: 1, needs: { wood: 4, iron: 1 }, workbench: true },
  { id: "lantern", makes: "lantern", count: 1, needs: { bulb: 1, glass: 1 } },
  { id: "sign", makes: "sign", count: 1, needs: { wood: 2 } },
  { id: "bed", makes: "bed", count: 1, needs: { wood: 6, fiber: 4 } },
  { id: "door", makes: "door", count: 1, needs: { wood: 3 } },
  { id: "crate", makes: "crate", count: 1, needs: { wood: 3 } },
  { id: "rope", makes: "rope", count: 1, needs: { fiber: 3 } },
  { id: "planter", makes: "planter", count: 1, needs: { wood: 1, rock: 1, fiber: 1 } },
  { id: "table", makes: "table", count: 1, needs: { wood: 3 } },
  { id: "stool", makes: "stool", count: 1, needs: { wood: 1 } },
  { id: "flag", makes: "flag", count: 1, needs: { wood: 1, fiber: 2 } },
  { id: "grapple", makes: "grapple", count: 1, needs: { rope: 2, iron: 1 }, workbench: true },
  // The first thing iron buys you: one smelt (2 ore, a coal) is a pack.
  { id: "steampack", makes: "steampack", count: 1, needs: { iron: 2, wood: 2, rope: 1 }, workbench: true },
  { id: "patchKit", makes: "patchKit", count: 2, needs: { wood: 2, iron: 1 }, workbench: true },
  // What the biomes give (see lifeasteroids.md, "Biomes"): each is worth the trip.
  { id: "barkRope", makes: "rope", count: 2, needs: { bark: 2 } },
  { id: "heartwood", makes: "wood", count: 12, needs: { heartwood: 1 } },
  { id: "sapPatch", makes: "patchKit", count: 2, needs: { sap: 2, wood: 1 } },
  { id: "sunGlass", makes: "glass", count: 6, needs: { sunGlass: 1 }, workbench: true },
  { id: "glowLantern", makes: "lantern", count: 1, needs: { glowcap: 2, glass: 1 } },
  { id: "dyedFlag", makes: "flag", count: 2, needs: { wood: 1, fiber: 1, lichen: 1 } },
  { id: "sandGlass", makes: "glass", count: 2, needs: { sand: 3, coal: 1 }, workbench: true },
  { id: "obsidianGlass", makes: "glass", count: 4, needs: { obsidian: 1 }, workbench: true },
  { id: "saltFish", makes: "saltFish", count: 1, needs: { skyfish: 1, salt: 1 } },
  { id: "sapling", makes: "sapling", count: 1, needs: { starbark: 1, spores: 1, moss: 1 } },
  // What the night gives (see INCOMERS).
  { id: "scrapIron", makes: "iron", count: 2, needs: { scrap: 4, coal: 1 }, workbench: true },
  { id: "wireRope", makes: "rope", count: 2, needs: { wire: 1 } },
  { id: "lensLamp", makes: "lensLamp", count: 1, needs: { lantern: 1, lens: 1, iron: 1 }, workbench: true },
  // Every building block in every family.
  ...FAMILIES.flatMap((family) =>
    BUILD_KINDS.map((b): Recipe => {
      const id = buildItem(family, b);
      const { needs, count, workbench } = BUILDS[b];
      return { id, makes: id, count, needs, ...(workbench ? { workbench } : {}) };
    }),
  ),
  // Every machine in every family, at a workbench.
  ...FAMILIES.flatMap((family) =>
    MACHINE_KINDS.map((m): Recipe => {
      const id = machineItem(family, m);
      return { id, makes: id, count: 1, needs: FAMILY_LOOK[family].needs(MACHINES[m].needs), workbench: true };
    }),
  ),
  // Every tool in every tier: a head of the tier's material on a wooden handle.
  ...TOOL_TIERS.flatMap((tier) =>
    TOOL_KINDS.map((kind): Recipe => {
      const id = toolItem(tier, kind);
      const head = TIER_MATERIAL[tier];
      const needs: Inventory = head === "wood" ? { wood: HEAD_SIZE[kind] + 2 } : { wood: 2, [head]: HEAD_SIZE[kind] };
      return { id, makes: id, count: 1, needs, workbench: true };
    }),
  ),
];

/** Why `recipe` can't be crafted right now, or null if it can. */
export function craftProblem(inv: Inventory, recipe: Recipe, atWorkbench: boolean): string | null {
  if (recipe.workbench && !atWorkbench) return "needs a workbench nearby";
  if (!hasAll(inv, recipe.needs)) return "missing materials";
  if (!roomFor(inv, { [recipe.makes]: recipe.count }, recipe.needs)) return "no room to carry it";
  return null;
}

/** The recipes that use `item`: what it's for, for the recipe book. */
export function usedIn(item: ItemId): Recipe[] {
  return RECIPES.filter((r) => (r.needs[item] ?? 0) > 0);
}

/** What a stoke takes from the inventory: the best fuel you have (whale oil, coal, starbark, then wood). */
export function fuelToStoke(inv: Inventory): ItemId | undefined {
  return (["whaleOil", "coal", "starbark", "wood"] as const).find((id) => count(inv, id) > 0);
}

/** What a stoke pours in for water: ice before water (it holds more). */
export function waterToStoke(inv: Inventory): ItemId | undefined {
  return (["ice", "water"] as const).find((id) => count(inv, id) > 0);
}

/** The tool an item is, if any (null for bare hands, blocks and materials). */
export function toolOf(item: ItemId | null | undefined): HeldTool | null {
  const def = item ? ITEMS[item] : undefined;
  return def?.tool && def.tier ? { kind: def.tool, tier: def.tier } : null;
}

/** How worn the top tool of each stack is: uses taken off it so far (out of TIERS[tier].uses). */
export type Wear = Partial<Record<ItemId, number>>;

/**
 * One use of `item`, if it's a tool: it wears, and when it's used up it breaks
 * (you have one fewer, and the next is fresh). Returns true if it broke.
 */
export function wearTool(inv: Inventory, wear: Wear, item: ItemId): boolean {
  const tier = ITEMS[item].tier;
  if (!tier || count(inv, item) < 1) return false;
  const used = (wear[item] ?? 0) + 1;
  if (used < TIERS[tier].uses) {
    wear[item] = used;
    return false;
  }
  delete wear[item];
  addItems(inv, { [item]: 1 }, -1);
  return true;
}

/** What's left of the top tool of a stack, 0..1 (1 for anything that isn't a tool). */
export function toolCondition(item: ItemId, wear: Wear): number {
  const tier = ITEMS[item].tier;
  return tier ? 1 - (wear[item] ?? 0) / TIERS[tier].uses : 1;
}
